import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import api from '../axios_helper';

const NATIVE_PUSH_TOKEN_STORAGE_KEY = 'gm:native-push-token';
const isAndroidAppBuild = process.env.REACT_APP_ANDROID_APP === 'true';

/** Keeps an opted-in Android device token current and routes notification taps. */
export default function NativePushManager() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAndroidAppBuild || !Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android' || !user?.id) {
      return undefined;
    }

    let disposed = false;
    let registrationListener;
    let registrationErrorListener;
    let actionListener;
    let appUrlOpenListener;

    const syncToken = async (tokenValue) => {
      const token = typeof tokenValue === 'string' ? tokenValue.trim() : '';
      if (!token) return;
      try { localStorage.setItem(NATIVE_PUSH_TOKEN_STORAGE_KEY, token); } catch (_) { /* storage unavailable */ }
      try {
        await api.post('/push/native/subscriptions', { token, supportsRichIcon: true });
      } catch (error) {
        console.warn('[Globe Memories] Could not sync the Android push token', error);
      }
    };

    const openAppUrl = (rawUrl) => {
      if (typeof rawUrl !== 'string') return;
      let target = rawUrl;
      if (!target.startsWith('/')) {
        try {
          const parsed = new URL(target);
          if (!['globememories.com', 'www.globememories.com'].includes(parsed.hostname)) return;
          target = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        } catch (_) {
          return;
        }
      }
      if (target.startsWith('/') && !target.startsWith('//')) navigate(target);
    };

    const openNotificationTarget = (event) => {
      const target = event?.notification?.data?.url || event?.notification?.data?.route;
      openAppUrl(target);
    };

    const setup = async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { App } = await import('@capacitor/app');
        appUrlOpenListener = await App.addListener('appUrlOpen', ({ url }) => openAppUrl(url));
        const launch = await App.getLaunchUrl();
        if (launch?.url) openAppUrl(launch.url);

        const { data: status } = await api.get('/push/native/status');
        if (disposed || !status?.enabled) return;

        const permissions = await PushNotifications.checkPermissions();
        if (permissions?.receive !== 'granted') return;

        registrationListener = await PushNotifications.addListener('registration', ({ value }) => {
          void syncToken(value);
        });
        registrationErrorListener = await PushNotifications.addListener('registrationError', (event) => {
          console.warn('[Globe Memories] Android push registration failed', event?.error);
        });
        actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', openNotificationTarget);

        let cachedToken = '';
        try { cachedToken = localStorage.getItem(NATIVE_PUSH_TOKEN_STORAGE_KEY) || ''; } catch (_) { /* storage unavailable */ }
        if (cachedToken && !status.registered) await syncToken(cachedToken);
        if (status.registered || cachedToken) {
          await PushNotifications.createChannel({
            id: 'gm_notifications',
            name: 'Globe Memories',
            description: 'Comentários, gostos, seguidores e novidades',
            importance: 4,
            vibration: true,
          }).catch(() => undefined);
          await PushNotifications.register();
        }
      } catch (error) {
        // An absent local Firebase config should not interrupt normal app use.
        console.warn('[Globe Memories] Android push setup is not ready', error?.message || error);
      }
    };

    void setup();
    return () => {
      disposed = true;
      registrationListener?.remove();
      registrationErrorListener?.remove();
      actionListener?.remove();
      appUrlOpenListener?.remove();
    };
  }, [navigate, user?.id]);

  return null;
}
